import { useCallback, useEffect, useState } from "react"
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import BookmarkIcon from "@mui/icons-material/BookmarkBorderOutlined"
import DeleteIcon from "@mui/icons-material/DeleteOutlineOutlined"

import { savedViewApi } from "../api"

/**
 * A filter combination, named and kept.
 *
 * ── Why a page with good filters still needs this ───────────────────────────
 *
 * The useful questions are combinations: "my overdue work", "everything
 * unassigned on Northwind", "this quarter's reconciliation, open only". Each
 * takes four or five controls to build, and something that takes five controls
 * every morning is something people stop asking. A view turns the combination
 * into one click — which is the whole difference between a filter bar somebody
 * configures once and one they actually use.
 *
 * ── Why the server and not the browser ──────────────────────────────────────
 *
 * localStorage is the obvious place, and it is wrong in a way that only shows
 * up later: a view saved at the desk does not exist on the laptop, and one
 * saved before a browser reset does not exist at all. Somebody who has curated
 * five views has done work, and work belongs somewhere that survives the
 * device.
 *
 * ── Why saving over a name replaces it ──────────────────────────────────────
 *
 * "Update this view" and "save a new one" are the same gesture as far as
 * anybody is concerned — you adjust the filters and save. Two rows called
 * "My overdue" is a list nobody can use, so the name is the identity and the
 * second save wins.
 */
export default function SavedViews({ page, filters, defaults, onApply }) {
  const [views, setViews] = useState([])
  const [anchor, setAnchor] = useState(null)
  const [name, setName] = useState("")
  const [busy, setBusy] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const load = useCallback(() => {
    setLoading(true)
    savedViewApi
      .list(page)
      .then((data) => setViews(data.views || []))
      .catch((err) => setError(err.userMessage))
      .finally(() => setLoading(false))
  }, [page])

  useEffect(() => {
    load()
  }, [load])

  /*
   * Nothing to save when nothing is set.
   *
   * A view of the default filters is a bookmark to the page you are already
   * on. Offering it produces a list of views that all do nothing, which is
   * worse than not offering it.
   */
  const changed = Object.keys(defaults).some((key) => {
    const current = filters[key]
    const base = defaults[key]
    return typeof base === "string" ? String(current ?? "").trim() !== base : Boolean(current) !== Boolean(base)
  })

  const save = async () => {
    const trimmed = name.trim()
    if (!trimmed) return
    setBusy(true)
    setError(null)
    try {
      await savedViewApi.save(page, trimmed, filters)
      setName("")
      load()
    } catch (err) {
      setError(err.userMessage)
    } finally {
      setBusy(false)
    }
  }

  const remove = async (id) => {
    setError(null)
    try {
      await savedViewApi.remove(id)
      setViews((current) => current.filter((view) => view.id !== id))
    } catch (err) {
      setError(err.userMessage)
    }
  }

  return (
    <>
      <Button
        size="small"
        color="inherit"
        startIcon={<BookmarkIcon />}
        onClick={(event) => setAnchor(event.currentTarget)}
      >
        {/* No `aria-label` here on purpose: one would REPLACE the accessible
            name, so the button would stop answering to the word written on it
            — which is what voice control and every test locator use. The
            contents already name it, and the count rides along in that name. */}
        Views
        {/* The count, because a button that might have five things behind it
            and might have none reads very differently in the two cases. */}
        {views.length > 0 ? (
          <Chip size="small" label={views.length} sx={{ ml: 0.75, height: 18, fontSize: 11, pointerEvents: "none" }} />
        ) : null}
      </Button>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { width: 300, maxWidth: "92vw", mt: 0.75, p: 1.5 } } }}
      >
        <Typography variant="subtitle2" gutterBottom>
          Saved views
        </Typography>

        {error ? (
          <Typography variant="caption" color="error" display="block" sx={{ mb: 1 }}>
            {error}
          </Typography>
        ) : null}

        {loading ? (
          <Stack alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={18} />
          </Stack>
        ) : views.length === 0 ? (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ py: 1 }}>
            None yet. Set the filters you want, then name them below.
          </Typography>
        ) : (
          <Stack spacing={0.25} sx={{ maxHeight: 220, overflowY: "auto", mb: 1 }}>
            {views.map((view) => (
              <Stack
                key={view.id}
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={{ borderRadius: 1.5, "&:hover": { bgcolor: "action.hover" } }}
              >
                <Box
                  component="button"
                  type="button"
                  onClick={() => {
                    onApply(view.filters)
                    setAnchor(null)
                  }}
                  sx={{
                    flex: 1,
                    textAlign: "left",
                    background: "none",
                    border: 0,
                    font: "inherit",
                    cursor: "pointer",
                    px: 1,
                    py: 0.75,
                    color: "text.primary",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {view.name}
                </Box>
                <Tooltip title={`Delete “${view.name}”`}>
                  <IconButton size="small" onClick={() => remove(view.id)} aria-label={`Delete ${view.name}`}>
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            ))}
          </Stack>
        )}

        <Divider sx={{ my: 1 }} />

        <Stack direction="row" spacing={0.75}>
          <TextField
            size="small"
            fullWidth
            value={name}
            disabled={!changed || busy}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") save()
            }}
            /* Said in the field rather than as a disabled button with no
               explanation: "set some filters first" is the whole reason it
               cannot be used, and a greyed-out control that does not say why
               is one people press twice and then give up on. */
            placeholder={changed ? "Name these filters…" : "Set some filters first"}
            inputProps={{ "aria-label": "Name for this view", maxLength: 80 }}
          />
          <Button size="small" variant="contained" disabled={!changed || !name.trim() || busy} onClick={save}>
            {busy ? "…" : "Save"}
          </Button>
        </Stack>
      </Popover>
    </>
  )
}
